/**
 * CopilotMessageList — exibe o histórico de mensagens da conversa (TASK-21).
 *
 * Renderiza mensagens de usuário e assistente. Durante SSE (`streamContent`
 * não vazio), exibe o conteúdo parcial como última mensagem do assistente.
 * Estado "pensando" (loading) exibe indicador de digitação.
 *
 * Referências: design.md §20.15, Req 9.2, TASK-21.
 */

import { useEffect, useRef } from "react";
import type { CopilotMessage } from "./copilotReducer.js";
import styles from "./Copilot.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CopilotMessageListProps {
  messages: readonly CopilotMessage[];
  /** Buffer parcial de tokens SSE em andamento (vazio quando não streaming). */
  streamContent: string;
  /** Se true, exibe indicador de "pensando" (estado loading). */
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Lista rolável de mensagens com scroll automático para a última.
 *
 * Req 9.2 — estado vazio exibe sugestões (renderizado no componente pai quando
 * não há mensagens; CopilotSuggestions fica no CopilotDrawer).
 */
export function CopilotMessageList({
  messages,
  streamContent,
  isLoading,
}: CopilotMessageListProps) {
  const endRef = useRef<HTMLDivElement>(null);

  // Rola para o final ao chegarem novas mensagens ou tokens.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, streamContent]);

  const hasStream = streamContent.length > 0;

  return (
    <div
      className={styles.messageList}
      role="log"
      aria-label="Histórico de mensagens"
      aria-live="polite"
      aria-atomic="false"
    >
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={[
            styles.message,
            msg.role === "user" ? styles.messageUser : styles.messageAssistant,
          ].join(" ")}
          role="article"
          aria-label={msg.role === "user" ? "Você" : "Azim AI"}
        >
          <span className={styles.messageRole} aria-hidden="true">
            {msg.role === "user" ? "Você" : "Azim AI"}
          </span>
          <p className={styles.messageContent}>{msg.content}</p>
        </div>
      ))}

      {/* Token parcial durante SSE (responding) — Finding F8:
          aria-hidden="true" durante streaming evita que o leitor de tela anuncie
          cada token incrementalmente (flooding). A mensagem final é adicionada a
          `messages` no DONE e anunciada normalmente pela live region (role=log).  */}
      {hasStream && (
        <div
          className={[styles.message, styles.messageAssistant].join(" ")}
          role="article"
          aria-label="Azim AI (respondendo)"
          aria-hidden="true"
          data-streaming="true"
        >
          <span className={styles.messageRole} aria-hidden="true">
            Azim AI
          </span>
          <p className={styles.messageContent}>
            {streamContent}
            <span className={styles.streamCursor} aria-hidden="true" />
          </p>
        </div>
      )}

      {/* Indicador de "pensando" (loading — antes do STREAM_START) */}
      {isLoading && !hasStream && (
        <div
          className={[styles.message, styles.messageAssistant].join(" ")}
          aria-label="Azim AI está pensando"
          role="status"
        >
          <span className={styles.messageRole} aria-hidden="true">
            Azim AI
          </span>
          <span className={styles.thinkingDots} aria-hidden="true">
            <span /><span /><span />
          </span>
        </div>
      )}

      {/* Âncora de scroll */}
      <div ref={endRef} aria-hidden="true" />
    </div>
  );
}
